/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftBadge from "components/SoftComponents/SoftBadge";

// Timeline context
import { useTimeline } from "modules/inspections/components/InspectionTimeline/context";

// Custom styles for the InspectionTimelineItem
import { timelineItem, timelineItemIcon } from "modules/inspections/components/InspectionTimeline/InspectionTimelineItem/styles";
import { Card } from "@mui/material";
import PhotoIcon from '@mui/icons-material/Photo';
import ConstructionIcon from '@mui/icons-material/Construction';

import SprayCan from "assets/images/spray_can_white.png";
import BrokenDevice from "assets/images/broken_device_white.png";

function InspectionTimelineItem({ itemData, color = "info", isLeft = false, icon, title, secondary_title = "", dateTime, description, badges, lastItem, selectedInspection, setSelectedInspection }) {
  const isDark = useTimeline();

  const renderBadges = badges.length > 0 ? badges.map((badge, key) => {
    const badgeKey = `badge-${key}`;
    return (
      <SoftBox key={badgeKey} mr={key === badges.length - 1 ? 0 : 0.5}>
        {badge.icon == "SprayCan" && (
          <SoftBox component="img" src={SprayCan} variant="gradient" bgColor={badge.color} sx={{ borderRadius: '4px', width: '24px', height: '24px' }} />
        )}
        {badge.icon == 'BrokenDevice' && (
          <SoftBox component="img" src={BrokenDevice} variant="gradient" bgColor={badge.color} sx={{ borderRadius: '4px', width: '24px', height: '24px' }} />
        )}
        {(badge.icon != "BrokenDevice" && badge.icon != "SprayCan") && (
          <SoftBox variant="gradient" bgColor={badge.color} sx={{ borderRadius: '4px', width: '24px', height: '24px' }}>
            <Icon fontSize="22px !important" sx={{ color: "#fff", fontSize: '22px', display: "flex", alignItems: "center", justifyContent: "center", width: '24px !important', height: '24px !important' }}>{badge.icon}</Icon>
          </SoftBox>
        )}
      </SoftBox>
    );
  }) : null;

  return (
    <SoftBox
      position="relative"
      width="100%"
      justifyContent={{ xxs: "flex-start", md: isLeft ? "flex-start" : "flex-end", xl: "flex-start", x3l: isLeft ? "flex-start" : "flex-end" }}
      sx={(theme) => timelineItem(theme, { isLeft, lastItem })}
    >
      <SoftBox
        bgColor={icon.color}
        width="1.625rem"
        height="1.625rem"
        borderRadius="50%"
        position="absolute"
        top="0.25rem"
        left="50%"
        zIndex={2}
        sx={(theme) => ({
          left: "2px",
          transform: "none",
          [theme.breakpoints.up("md")]: {
            left: "50%",
            transform: "translateX(-50%)",
          },
          [theme.breakpoints.up("xl")]: {
            left: "2px",
            transform: "none",
          },
          [theme.breakpoints.up("x3l")]: {
            left: "50%",
            transform: "translateX(-50%)",
          },
        })}
      >
        <Icon sx={(theme) => timelineItemIcon(theme, { color: isDark ? "dark" : "white" })}>{icon.icon}</Icon>
      </SoftBox>

      <SoftBox
        display="flex"
        justifyContent={isLeft ? "flex-start" : "flex-end"}
        width="100%"
        pl={{ xxs: "45px", md: isLeft ? 0 : "calc(50% + 1.5rem)", xl: "45px", x3l: isLeft ? 0 : "calc(50% + 1.5rem)" }}
        pr={{ xxs: 0, md: isLeft ? "calc(50% + 1.5rem)" : 0, xl: 0, x3l: isLeft ? "calc(50% + 1.5rem)" : 0 }}
      >
        <SoftBox /*ml={5.75} pt={description ? 0.7 : 0.5} lineHeight={0}*/ width="100%"
          sx={{
            cursor: 'pointer'
          }}
        >
          <Card onClick={() => setSelectedInspection(itemData)}
            sx={{

              boxShadow: selectedInspection?._id == itemData._id ? "0 0 15px 2px rgba(0, 0, 0, 0.3)" : "none",

              "&:hover": {
                boxShadow: "0 0 15px 2px rgba(0, 0, 0, 0.3)",
              },
              transition: "box-shadow 300ms ease-in-out",
            }}
          >
            <SoftBox pt={2} pb={!badges.length ? 2 : 1.5} pr={2} pl={2}>
              {!secondary_title.trim().length ? (
                <SoftTypography variant="button" fontWeight="medium" color={isDark ? "white" : "dark"}>
                  {title}
                </SoftTypography>
              ) : (
                <SoftBox display="flex" flexDirection="column">
                  <SoftBox sx={{ opacity: itemData.record_type == "inspection" ? 1 : 0.5 }}>
                    <SoftTypography variant="button" fontWeight="medium" color={isDark ? "white" : "dark"}>{title}</SoftTypography>
                  </SoftBox>
                  <SoftBox display="flex" flexDirection="row" sx={{ opacity: itemData.record_type == "inspection" ? 0.5 : 1 }}>
                    <Icon>subdirectory_arrow_right</Icon>
                    <SoftTypography pl={0.5} variant="button" fontWeight="medium" color={isDark ? "white" : "dark"}>{secondary_title}</SoftTypography>
                  </SoftBox>
                </SoftBox>
              )}
              {itemData.images[0]?.paths?.length > 0 && (
                <SoftBox
                  sx={{
                    position: 'absolute',
                    right: '12px',
                    top: '12px',
                    opacity: '0.6'
                  }}
                >
                  <SoftBox
                    py={'2px'}
                    px={'5px'}
                    display="flex"
                    justifyContent="center"
                    sx={{
                      position: 'absolute',
                      right: '-3px',
                      top: '-3px',
                      backgroundColor: '#000',
                      borderRadius: '100px'
                    }}
                  >
                    <SoftTypography color="light" variant="caption">
                      {itemData.images[0]?.paths?.length}
                    </SoftTypography>
                  </SoftBox>
                  <PhotoIcon sx={(theme) => ({
                    width: '25px',
                    height: '25px',

                    [theme.breakpoints.up("md")]: {
                      width: '30px',
                      height: '30px'
                    },
                    [theme.breakpoints.up("xl")]: {
                      width: '35px',
                      height: '35px'
                    },
                    [theme.breakpoints.up("x3l")]: {
                      width: '40px',
                      height: '40px'
                    },
                  })}
                  />
                </SoftBox>
              )}
              {(itemData.rectification != null || itemData.record_type == "rectification") && (
                <SoftBox
                  sx={{
                    position: 'absolute',
                    right: '12px',
                    top: '52px',
                    opacity: '0.6'
                  }}
                >
                  <ConstructionIcon sx={(theme) => ({
                    width: '25px',
                    height: '25px',

                    [theme.breakpoints.up("md")]: {
                      width: '30px',
                      height: '30px'
                    },
                    [theme.breakpoints.up("xl")]: {
                      width: '35px',
                      height: '35px'
                    },
                    [theme.breakpoints.up("x3l")]: {
                      width: '40px',
                      height: '40px'
                    },
                  })}
                  />
                </SoftBox>
              )}
              <SoftBox mt={0.5}>
                <SoftTypography
                  variant="caption"
                  fontWeight="medium"
                  color={isDark ? "secondary" : "text"}
                >
                  {dateTime}
                </SoftTypography>
              </SoftBox>
              <SoftBox mt={2}>
                {description ? (
                  <SoftTypography variant="button" fontWeight="regular" color="text">
                    {description}
                  </SoftTypography>
                ) : null}
              </SoftBox>
              <SoftBox mb={badges.length > 0 ? 2 : 0}>
                {itemData.technician ? (
                  <SoftTypography variant="caption" textTransform="capitalize" fontWeight="regular" color="text">
                    {itemData?.record_type == "rectification" ? `Rectified by: ${itemData.technician}` : `Inspected by: ${itemData.technician}`}
                  </SoftTypography>
                ) : null}
              </SoftBox>
              {badges.length > 0 ? (
                <SoftBox display="flex">
                  {renderBadges}
                </SoftBox>
              ) : null}
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>
    </SoftBox >
  );
}

// Typechecking props for the InspectionTimelineItem
InspectionTimelineItem.propTypes = {
  itemData: PropTypes.object,
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ]),
  isLeft: PropTypes.bool,
  icon: PropTypes.object,
  title: PropTypes.string,
  secondary_title: PropTypes.string,
  dateTime: PropTypes.string,
  description: PropTypes.string,
  badges: PropTypes.arrayOf(PropTypes.object),
  lastItem: PropTypes.bool,
  selectedInspection: PropTypes.object,
  setSelectedInspection: PropTypes.func,
};

export default InspectionTimelineItem;
